import { STATUS_META, STATUS_ORDER, PRIORITY_META, CATEGORY_META, fmtDate } from "@/lib/constants";

const LOGO = "https://customer-assets-v7afamib.emergentagent.net/job_archi-interior-hub/artifacts/jmvnezz2_image.png";

const esc = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

// Opens a print-friendly window and triggers the browser's print-to-PDF dialog.
export function printProject({ project, tasks, memberMap }) {
  const meta = CATEGORY_META[project.category] || {};
  const nameOf = (uid) => memberMap[uid]?.name || "—";

  let clTotal = 0;
  let clDone = 0;
  tasks.forEach((t) =>
    (t.checklist || []).forEach((c) => {
      clTotal += 1;
      if (c.done) clDone += 1;
    })
  );
  const clPct = clTotal ? Math.round((clDone / clTotal) * 100) : 0;

  const grouped = {};
  STATUS_ORDER.forEach((s) => (grouped[s] = []));
  tasks.forEach((t) => (grouped[t.status] = grouped[t.status] || []).push(t));

  const statusChips = STATUS_ORDER.map((s) => {
    const m = STATUS_META[s];
    const n = (grouped[s] || []).length;
    return `<span class="chip" style="background:${m.bg};color:${m.color}">${esc(m.label)} · ${n}</span>`;
  }).join("");

  const taskBlocks = STATUS_ORDER.map((s) => {
    const list = grouped[s] || [];
    if (!list.length) return "";
    const m = STATUS_META[s];
    const rows = list
      .map((t) => {
        const assignees = (t.assigned_to || []).map(nameOf).join(", ") || "—";
        const prio =
          t.priority && t.priority !== "none"
            ? `<span style="color:${PRIORITY_META[t.priority].color};font-weight:600">${esc(PRIORITY_META[t.priority].label)}</span>`
            : "—";
        const cl = t.checklist || [];
        const clStr = cl.length ? `${cl.filter((c) => c.done).length}/${cl.length}` : "—";
        const checklist = cl.length
          ? `<div class="cl">${cl
              .map(
                (c) =>
                  `<div class="cl-item"><span class="box ${c.done ? "on" : ""}">${c.done ? "✓" : ""}</span>${esc(c.text || c.name || "")}${
                    c.assignee ? `<span class="cl-meta"> · ${esc(nameOf(c.assignee))}</span>` : ""
                  }${c.due_date ? `<span class="cl-meta"> · due ${esc(fmtDate(c.due_date))}</span>` : ""}</div>`
              )
              .join("")}</div>`
          : "";
        return `<tr>
            <td class="tname">${esc(t.name)}${checklist}</td>
            <td>${esc(assignees)}</td>
            <td>${esc(fmtDate(t.start_date))}</td>
            <td>${esc(fmtDate(t.end_date))}</td>
            <td>${prio}</td>
            <td>${clStr}</td>
          </tr>`;
      })
      .join("");
    return `<div class="group">
        <div class="group-h" style="background:${m.bg};color:${m.color}">${esc(m.label)} <span>${list.length}</span></div>
        <table class="tbl">
          <thead><tr><th>Task</th><th>Assigned To</th><th>Start</th><th>End</th><th>Priority</th><th>Checklist</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>`;
  }).join("");

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(project.name)} — TWP co</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: 'Helvetica Neue', Arial, sans-serif; color: #18181b; margin: 32px; }
    .head { display:flex; align-items:center; gap:16px; border-bottom:2px solid #18181b; padding-bottom:16px; margin-bottom:20px; }
    .head img { height:52px; width:auto; }
    .brand { font-size:11px; letter-spacing:.2em; text-transform:uppercase; color:#71717a; }
    h1 { font-size:26px; margin:0 0 4px; letter-spacing:-0.02em; }
    .cat { font-size:12px; text-transform:uppercase; letter-spacing:.12em; color:#71717a; margin:0; }
    .desc { color:#52525b; font-size:13px; margin:12px 0 0; max-width:640px; }
    .summary { display:flex; flex-wrap:wrap; gap:8px; margin:18px 0 8px; align-items:center; }
    .chip { font-size:11px; font-weight:600; padding:4px 10px; border-radius:2px; }
    .progress { margin:14px 0 24px; font-size:12px; color:#52525b; }
    .bar { height:8px; background:#e4e4e7; border-radius:4px; margin-top:6px; overflow:hidden; }
    .bar > span { display:block; height:100%; background:#059669; }
    .group { margin-bottom:18px; page-break-inside:avoid; }
    .group-h { font-size:12px; font-weight:700; text-transform:uppercase; letter-spacing:.08em; padding:6px 10px; }
    .group-h span { opacity:.7; margin-left:4px; }
    table.tbl { width:100%; border-collapse:collapse; font-size:12px; }
    .tbl th { text-align:left; font-size:9px; text-transform:uppercase; letter-spacing:.1em; color:#a1a1aa; padding:6px 8px; border-bottom:1px solid #e4e4e7; }
    .tbl td { padding:8px; border-bottom:1px solid #f4f4f5; vertical-align:top; }
    .tname { font-weight:600; }
    .cl { margin-top:6px; }
    .cl-item { font-size:11px; color:#52525b; font-weight:400; display:flex; align-items:center; gap:6px; margin:2px 0; }
    .cl-meta { color:#a1a1aa; }
    .box { display:inline-flex; align-items:center; justify-content:center; width:13px; height:13px; border:1px solid #a1a1aa; border-radius:2px; font-size:9px; color:#059669; }
    .box.on { border-color:#059669; }
    .foot { margin-top:28px; font-size:10px; color:#a1a1aa; border-top:1px solid #e4e4e7; padding-top:10px; }
    @media print { body { margin:16px; } }
  </style></head><body>
    <div class="head">
      <img src="${LOGO}" alt="TWP co" />
      <div>
        <div class="brand">TWP co · Bringing design to life</div>
      </div>
    </div>
    <p class="cat">${esc(meta.label || "")} · ${esc(meta.sub || "")}${project.builder ? " · " + esc(project.builder) : ""}</p>
    <h1>${esc(project.name)}</h1>
    ${project.description ? `<p class="desc">${esc(project.description)}</p>` : ""}
    <div class="summary">${statusChips}</div>
    <div class="progress">Checklist progress: <strong>${clDone}/${clTotal}</strong> (${clPct}%)
      <div class="bar"><span style="width:${clPct}%"></span></div>
    </div>
    ${taskBlocks || '<p style="color:#a1a1aa;font-size:13px">No tasks in this project.</p>'}
    <div class="foot">Generated ${new Date().toLocaleString()} · ${tasks.length} task${tasks.length !== 1 ? "s" : ""} · TWP co Project Manager</div>
  </body></html>`;

  // Render into a sandboxed Blob URL (no document.write) to avoid any XSS surface;
  // all interpolated values are already HTML-escaped via esc().
  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, "_blank");
  if (!w) {
    URL.revokeObjectURL(url);
    return false;
  }
  const cleanup = () => URL.revokeObjectURL(url);
  w.addEventListener("load", () => {
    w.focus();
    setTimeout(() => {
      try { w.print(); } catch (e) { console.error("Print failed:", e); }
      cleanup();
    }, 350);
  });
  return true;
}
